import React, { useState } from 'react';
import { X, AlertCircle, Camera } from 'lucide-react';

interface NoSaleReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string, photoUrl?: string) => void;
  outletName: string;
}

export type NoSaleReason = 'outlet_not_open' | 'shelf_full' | 'rate_competition' | 'quality_service';

const NoSaleReasonModal: React.FC<NoSaleReasonModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  outletName
}) => {
  const [selectedReason, setSelectedReason] = useState<NoSaleReason | ''>('');
  const [showShelfPhotoModal, setShowShelfPhotoModal] = useState(false);
  const [shelfPhotoUrl, setShelfPhotoUrl] = useState<string>('');

  const reasonOptions: { value: NoSaleReason; label: string; description: string }[] = [
    {
      value: 'outlet_not_open',
      label: 'Outlet Not Open',
      description: 'The outlet was closed during the visit'
    },
    {
      value: 'shelf_full',
      label: 'Shelf Full',
      description: 'Outlet already has sufficient stock'
    },
    {
      value: 'rate_competition',
      label: 'Rate / Competition Issue',
      description: 'Price concerns or competitor preference'
    },
    {
      value: 'quality_service',
      label: 'Quality / Service Issue',
      description: 'Product quality or service concerns'
    }
  ];

  const handleReasonSelect = (reason: NoSaleReason) => {
    setSelectedReason(reason);
    
    // If "Shelf Full" is selected, show photo modal
    if (reason === 'shelf_full') {
      setShowShelfPhotoModal(true);
    }
  };

  const handleSubmit = () => {
    if (!selectedReason) return;
    
    // For shelf full, require photo proof
    if (selectedReason === 'shelf_full' && !shelfPhotoUrl) {
      alert('Please take a photo of the shelf as proof');
      setShowShelfPhotoModal(true);
      return;
    }
    
    onSubmit(selectedReason, shelfPhotoUrl);
    
    // Reset state
    setSelectedReason('');
    setShelfPhotoUrl('');
    setShowShelfPhotoModal(false);
  };

  const handleShelfPhotoTaken = (photoUrl: string) => {
    setShelfPhotoUrl(photoUrl);
    setShowShelfPhotoModal(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">No Sale Reason</h3>
            <p className="text-sm text-gray-600">{outletName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div className="flex items-center space-x-2 text-orange-600">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm font-medium">Please select a reason for no sale</span>
          </div>

          {/* Reason Options */}
          <div className="space-y-2">
            {reasonOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleReasonSelect(option.value)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  selectedReason === option.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedReason === option.value
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}>
                    {selectedReason === option.value && (
                      <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">{option.label}</div>
                    <div className="text-sm text-gray-600">{option.description}</div>
                  </div>
                  {option.value === 'shelf_full' && (
                    <Camera className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Shelf Photo Status */}
          {selectedReason === 'shelf_full' && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Camera className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-800">
                    Shelf Photo {shelfPhotoUrl ? 'Captured' : 'Required'}
                  </span>
                </div>
                {!shelfPhotoUrl && (
                  <button
                    onClick={() => setShowShelfPhotoModal(true)}
                    className="text-sm text-yellow-600 hover:text-yellow-700 font-medium"
                  >
                    Take Photo
                  </button>
                )}
              </div>
              {shelfPhotoUrl && (
                <div className="mt-2">
                  <img
                    src={shelfPhotoUrl}
                    alt="Shelf photo"
                    className="w-16 h-16 object-cover rounded border"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedReason}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                selectedReason
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Submit No Sale
            </button>
          </div>
        </div>

        {/* Shelf Photo Modal */}
        {showShelfPhotoModal && (
          <ShelfPhotoModal
            isOpen={showShelfPhotoModal}
            onClose={() => setShowShelfPhotoModal(false)}
            onPhotoTaken={handleShelfPhotoTaken}
            outletName={outletName}
          />
        )}
      </div>
    </div>
  );
};

// Shelf Photo Modal Component
interface ShelfPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotoTaken: (photoUrl: string) => void;
  outletName: string;
}

const ShelfPhotoModal: React.FC<ShelfPhotoModalProps> = ({
  isOpen,
  onClose,
  onPhotoTaken,
  outletName
}) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string>('');

  const handleTakePhoto = () => {
    setIsCapturing(true);
    
    // Simulate camera capture for now
    // In a real implementation, you would use the device camera API
    setTimeout(() => {
      const mockPhotoUrl = `https://via.placeholder.com/300x200?text=Shelf+Photo+${Date.now()}`;
      setPhotoUrl(mockPhotoUrl);
      setIsCapturing(false);
    }, 1000);
  };

  const handleConfirmPhoto = () => {
    if (photoUrl) {
      onPhotoTaken(photoUrl);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60 p-4">
      <div className="bg-white rounded-lg w-full max-w-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Shelf Photo</h3>
            <p className="text-sm text-gray-600">{outletName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div className="text-center">
            <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">
              Take a photo of the shelf as proof that it's already full
            </p>
          </div>

          {/* Camera View / Photo Preview */}
          <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
            {isCapturing ? (
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Taking photo...</p>
              </div>
            ) : photoUrl ? (
              <img
                src={photoUrl}
                alt="Shelf photo"
                className="w-full h-full object-cover rounded-lg"
              />
            ) : (
              <div className="text-center">
                <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Camera view will appear here</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          {!photoUrl ? (
            <button
              onClick={handleTakePhoto}
              disabled={isCapturing}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {isCapturing ? 'Taking Photo...' : 'Take Photo'}
            </button>
          ) : (
            <div className="flex space-x-3">
              <button
                onClick={() => setPhotoUrl('')}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Retake
              </button>
              <button
                onClick={handleConfirmPhoto}
                className="flex-1 py-2 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                Use Photo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoSaleReasonModal;
